import { useEffect, useState } from 'react';
import {
  Address as ApiAddress,
  CustomerSigner,
  ProjectType,
  SettingKey,
} from '@bulthaup/api/db';
import { getProjectAddresses } from '@bulthaup/api/entities/project';
import {
  Button,
  Card,
  Checkbox,
  Collapse,
  Divider,
  Group,
  Radio,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import {
  IconChevronDown,
  IconDeviceFloppy,
  IconTrash,
} from '@tabler/icons-react';
import { merge } from 'lodash';
import { z } from 'zod';

import { useUser } from '../../../../hooks/use-user';
import { trpc } from '../../../../services/trpc';
import { handleFormError } from '../../../../utils/handle-form-error';
import { label } from '../../../../utils/label';
import { Address, AddressFields } from './-address-fields';

interface ProjectFormValues {
  name: string;
  type: string;
  shopId: string;
  sellerId: string;
  installerId?: string;
  technicianId?: string;
  customerId?: string;
  shouldCreateNewCustomer?: boolean;
  signer: CustomerSigner;
  customer1Title: string;
  customer1FirstName: string;
  customer1LastName: string;
  customer1Email: string;
  customer1Phone: string;
  customer2Title: string;
  customer2FirstName: string;
  customer2LastName: string;
  customer2Email: string;
  customer2Phone: string;
  companyName: string;

  contacts: Array<{
    id?: string;
    type: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    quoteNotificationEnabled: boolean;
    technicalPlanNotificationEnabled: boolean;
    constructionSiteReviewNotificationEnabled: boolean;
    constructionSiteReportNotificationEnabled: boolean;
    address: Address;
  }>;

  homeAddress: Address;
  deliveryAddress: Address;
  installationAddress: Address;
  billingAddress: Address;
  isDeliveryAddressOverride: boolean;
  isInstallationAddressOverride: boolean;
  isBillingAddressOverride: boolean;
}

interface ProjectFormProps {
  onSubmit: (values: ProjectFormValues) => void;
  initialValues?: ProjectFormValues;
}

// RouterInput["projects"]["create"] is the type that the backend expects
// ProjectFormValues is the type that the frontend form works with
export function ProjectForm({ onSubmit, initialValues }: ProjectFormProps) {
  const [clientType, setClientType] = useState<
    'new' | 'existing' | 'editing' | undefined
  >(initialValues ? 'editing' : undefined);

  const [isLoading, setIsLoading] = useState(false);
  const [showCustomer2, setShowCustomer2] = useState(
    !!initialValues?.customer2FirstName,
  );

  const [showCompany, setShowCompany] = useState(!!initialValues?.companyName);
  const [expandedContacts, setExpandedContacts] = useState<
    Record<number, boolean>
  >({});
  const { data: shops } = trpc.projects.listShops.useQuery();
  const { data: sellers } = trpc.projects.listSellers.useQuery();
  const { data: technicians } = trpc.projects.listTechnicians.useQuery();
  const { data: installers } = trpc.projects.listInstallers.useQuery();
  const { user } = useUser();
  const { data: titles } = trpc.settings.get.useQuery({
    key: SettingKey.CUSTOMER_TITLES,
  });

  const form = useForm<ProjectFormValues>({
    initialValues: merge(
      {
        customerId: '',
        shouldCreateNewCustomer: false,
        name: '',
        signer: CustomerSigner.CLIENT1,
        customer1Title: '',
        customer1FirstName: '',
        customer1LastName: '',
        customer1Email: '',
        customer1Phone: '',
        customer2Title: '',
        customer2FirstName: '',
        customer2LastName: '',
        customer2Email: '',
        customer2Phone: '',
        companyName: '',
        type: '',
        shopId: user.shops[0]?.id || '',
        sellerId: user.type === 'SELLER' ? user.id : '',
        installerId: '',
        technicianId: '',
        contacts: [],
        homeAddress: {
          address1: '',
          address2: '',
          address3: '',
          city: '',
          postalCode: '',
          code1: '',
          code2: '',
          floor: '',
          elevator: '',
        },
        deliveryAddress: {
          address1: '',
          address2: '',
          address3: '',
          city: '',
          postalCode: '',
          code1: '',
          code2: '',
          floor: '',
          elevator: '',
        },
        installationAddress: {
          address1: '',
          address2: '',
          address3: '',
          city: '',
          postalCode: '',
          code1: '',
          code2: '',
          floor: '',
          elevator: '',
        },
        billingAddress: {
          address1: '',
          address2: '',
          address3: '',
          city: '',
          postalCode: '',
          code1: '',
          code2: '',
          floor: '',
          elevator: '',
        },
        isDeliveryAddressOverride: false,
        isInstallationAddressOverride: false,
        isBillingAddressOverride: false,
      },
      initialValues || {},
    ),
    validate: zodResolver(
      z.union([
        z.object({
          customerId: z.string().min(1, 'Client requis'),
          type: z.nativeEnum(ProjectType, {
            message: 'Type de projet requis',
          }),
          shopId: z.string().min(1, 'Magasin requis'),
          sellerId: z.string().min(1, 'Vendeur requis'),
        }),
        z.object({
          customer1FirstName: z.string().min(1, 'Prénom requis'),
          customer1LastName: z.string().min(1, 'Nom requis'),
          customer1Email: z.string().email('Email invalide'),
          customer1Phone: z.string().min(1, 'Téléphone requis'),
          type: z.nativeEnum(ProjectType, {
            message: 'Type de projet requis',
          }),
          shopId: z.string().min(1, 'Magasin requis'),
          sellerId: z.string().min(1, 'Vendeur requis'),
        }),
      ]),
    ),
    transformValues: values => ({
      ...values,
      type: values.type as ProjectType,
    }),
  });

  const { data: customers } = trpc.projects.listCustomers.useQuery(
    {
      shopId: form.values.shopId,
    },
    {
      enabled: !!form.values.shopId,
    },
  );

  // Clear fields when checkboxes are unchecked
  useEffect(() => {
    if (!showCustomer2) {
      form.setFieldValue('customer2Title', '');
      form.setFieldValue('customer2FirstName', '');
      form.setFieldValue('customer2LastName', '');
      form.setFieldValue('customer2Email', '');
      form.setFieldValue('customer2Phone', '');
    }
  }, [showCustomer2]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showCompany) {
      form.setFieldValue('companyName', '');
    }
  }, [showCompany]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (values: ProjectFormValues) => {
    try {
      setIsLoading(true);
      await onSubmit(values);
    } catch (error) {
      handleFormError(form, error);
    } finally {
      setIsLoading(false);
    }
  };
  const availableSellers =
    sellers && technicians
      ? [
          ...sellers.filter(seller =>
            seller.shops.some(shop => shop.id === form.values.shopId),
          ),
          ...technicians.filter(technician =>
            technician.shops.some(shop => shop.id === form.values.shopId),
          ),
        ]
      : [];

  const selectedShopTechnicians = technicians?.filter(technician =>
    technician.shops.some(shop => shop.id === form.values.shopId),
  );

  form.watch('shopId', () => {
    if (availableSellers?.length === 1) {
      form.setFieldValue('sellerId', availableSellers[0].id);
    }
    if (selectedShopTechnicians?.length === 1) {
      form.setFieldValue('technicianId', selectedShopTechnicians[0].id);
    }
  });

  const computedAddresses = getProjectAddresses({
    isDeliveryAddressOverride: form.values.isDeliveryAddressOverride,
    isInstallationAddressOverride: form.values.isInstallationAddressOverride,
    isBillingAddressOverride: form.values.isBillingAddressOverride,
    homeAddress: form.values.homeAddress as ApiAddress,
    deliveryAddress: form.values.deliveryAddress as ApiAddress,
    installationAddress: form.values.installationAddress as ApiAddress,
    billingAddress: form.values.billingAddress as ApiAddress,
  });

  form.watch('isDeliveryAddressOverride', () => {
    form.setFieldValue('deliveryAddress', {
      ...computedAddresses.deliveryAddress,
      id: form.values.deliveryAddress.id,
    });
  });

  form.watch('isInstallationAddressOverride', () => {
    form.setFieldValue('installationAddress', {
      ...computedAddresses.installationAddress,
      id: form.values.installationAddress.id,
    });
  });

  form.watch('isBillingAddressOverride', () => {
    form.setFieldValue('billingAddress', {
      ...computedAddresses.billingAddress,
      id: form.values.billingAddress.id,
    });
  });

  form.watch('shopId', () => {
    form.setFieldValue('customerId', '');
    setClientType(undefined);
  });

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <Stack>
          <Title order={4}>Projet</Title>
          <Group className="">
            <TextInput
              required
              label="Nom du projet"
              {...form.getInputProps('name')}
            />
            <Select
              required
              label="Type de projet"
              placeholder="Sélectionner un type de projet"
              data={Object.values(ProjectType).map(type => ({
                value: type,
                label: label(type) || '',
              }))}
              {...form.getInputProps('type')}
            />

            {user.type !== 'SELLER' && (
              <>
                <Select
                  required
                  label="Magasin"
                  placeholder="Sélectionner un magasin"
                  data={shops?.map(shop => ({
                    value: shop.id,
                    label: shop.name,
                  }))}
                  searchable
                  clearable
                  nothingFoundMessage="Aucun magasin trouvé"
                  {...form.getInputProps('shopId')}
                />
                {form.values.shopId ? (
                  <>
                    <Select
                      key={form.values.shopId + 'seller'}
                      required
                      label="Vendeur"
                      placeholder="Sélectionner un vendeur"
                      disabled={form.values.shopId == null}
                      data={availableSellers?.map(user => ({
                        value: user.id,
                        label: user.firstName + ' ' + user.lastName,
                      }))}
                      searchable
                      clearable
                      nothingFoundMessage="Aucun vendeur trouvé"
                      {...form.getInputProps('sellerId')}
                    />
                    <Select
                      key={form.values.shopId + 'technician'}
                      label="Technicien"
                      placeholder="Sélectionner un technicien"
                      disabled={form.values.shopId == null}
                      data={selectedShopTechnicians?.map(technician => ({
                        value: technician.id,
                        label: technician.firstName + ' ' + technician.lastName,
                      }))}
                      searchable
                      clearable
                      nothingFoundMessage="Aucun technicien trouvé"
                      description="Pour le plan technique"
                      {...form.getInputProps('technicianId')}
                    />
                  </>
                ) : (
                  <Select
                    disabled
                    key="none"
                    required
                    label="Vendeur"
                    placeholder="Sélectionner un vendeur"
                  />
                )}
                <Select
                  label="Poseur"
                  placeholder="Sélectionner un poseur"
                  data={[...(technicians || []), ...(installers || [])].map(
                    user => ({
                      value: user.id,
                      label: user.firstName + ' ' + user.lastName,
                    }),
                  )}
                  searchable
                  clearable
                  nothingFoundMessage="Aucun poseur trouvé"
                  description="Pour les PV de chantier"
                  {...form.getInputProps('installerId')}
                />
              </>
            )}
          </Group>
          {!initialValues && form.values.shopId && (
            <Group my="md">
              <Radio
                label="Nouveau client"
                value="new"
                checked={clientType === 'new'}
                onChange={() => setClientType('new')}
              />
              <Radio
                label="Client existant"
                value="existing"
                checked={clientType === 'existing'}
                onChange={() => setClientType('existing')}
              />
            </Group>
          )}
        </Stack>

        {clientType === 'existing' && (
          <Stack>
            <Title order={4}>Client existant</Title>
            <Select
              searchable
              placeholder="Sélectionner un client"
              data={customers?.map(c => ({
                value: c.id,
                label:
                  `${c.customer1Title} ${c.customer1FirstName} ${c.customer1LastName} - ${c.projects[0]?.name}`.trim(),
              }))}
              clearable
              nothingFoundMessage="Aucun client trouvé"
              onChange={value => {
                form.setFieldValue('customerId', value || '');
              }}
            />
            <Switch
              label="Créer un nouveau projet en copiant les informations du client existant"
              checked={form.values.shouldCreateNewCustomer}
              onChange={() => {
                form.setFieldValue(
                  'shouldCreateNewCustomer',
                  !form.values.shouldCreateNewCustomer,
                );
              }}
            />
          </Stack>
        )}

        {(clientType === 'new' || clientType === 'editing') && (
          <>
            <Divider my="md" />
            {/* Client Section */}
            <Stack>
              <Title order={4}>Client</Title>
              <Group mt="md" gap="xl" mb="md">
                <Checkbox
                  label="Couple"
                  checked={showCustomer2}
                  onChange={event => {
                    setShowCustomer2(event.currentTarget.checked);
                    form.setFieldValue('signer', CustomerSigner.CLIENT1);
                  }}
                />
                <Checkbox
                  label="Entreprise"
                  checked={showCompany}
                  onChange={event =>
                    setShowCompany(event.currentTarget.checked)
                  }
                />
              </Group>

              <Group grow>
                <Title order={6}>Client 1</Title>
                <Select
                  label="Civilité"
                  data={titles?.value as string[]}
                  {...form.getInputProps('customer1Title')}
                />
                <TextInput
                  label="Prénom"
                  {...form.getInputProps('customer1FirstName')}
                  required
                />
                <TextInput
                  label="Nom"
                  {...form.getInputProps('customer1LastName')}
                  required
                />
                <TextInput
                  label="Email"
                  {...form.getInputProps('customer1Email')}
                  required
                />
                <TextInput
                  label="Téléphone"
                  {...form.getInputProps('customer1Phone')}
                  required
                />
              </Group>
              {showCustomer2 && (
                <Group grow>
                  <Title order={6}>Client 2</Title>
                  <Select
                    label="Civilité"
                    data={titles?.value as string[]}
                    {...form.getInputProps('customer2Title')}
                  />
                  <TextInput
                    label="Prénom"
                    {...form.getInputProps('customer2FirstName')}
                    required
                  />
                  <TextInput
                    label="Nom"
                    {...form.getInputProps('customer2LastName')}
                    required
                  />
                  <TextInput
                    label="Email"
                    {...form.getInputProps('customer2Email')}
                    required
                  />
                  <TextInput
                    label="Téléphone"
                    {...form.getInputProps('customer2Phone')}
                  />
                </Group>
              )}
            </Stack>
            {showCompany && (
              <>
                <Group grow>
                  <Title order={6}>Entreprise</Title>
                  <TextInput
                    label="Nom de l'entreprise"
                    {...form.getInputProps('companyName')}
                  />
                </Group>
              </>
            )}
            {showCustomer2 && (
              <Group mt="md">
                <Select
                  allowDeselect={false}
                  label="Interlocuteur qui recevra les demandes de signature"
                  data={[CustomerSigner.CLIENT1, CustomerSigner.CLIENT2]
                    .filter(contact => {
                      if (contact === CustomerSigner.CLIENT1) return true;
                      if (contact === CustomerSigner.CLIENT2)
                        return showCustomer2;
                      return false;
                    })
                    .map(contact => ({
                      value: contact,
                      label: label(contact) || '',
                    }))}
                  {...form.getInputProps('signer')}
                />
              </Group>
            )}

            <Divider my="md" />

            <Stack>
              <Group justify="apart">
                <Title order={4}>Contacts</Title>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => {
                    const contacts = form.values.contacts || [];
                    form.setFieldValue('contacts', [
                      ...contacts,
                      {
                        type: '',
                        firstName: '',
                        lastName: '',
                        email: '',
                        phone: '',
                        quoteNotificationEnabled: false,
                        technicalPlanNotificationEnabled: false,
                        constructionSiteReviewNotificationEnabled: false,
                        constructionSiteReportNotificationEnabled: false,
                        address: {
                          address1: '',
                          address2: '',
                          address3: '',
                          city: '',
                          postalCode: '',
                          code1: '',
                          code2: '',
                          floor: '',
                          elevator: '',
                        },
                      },
                    ]);
                  }}
                >
                  Ajouter un contact
                </Button>
              </Group>

              {(form.values.contacts || []).map((contact, index) => (
                <Card key={index} shadow="xs" p="md" withBorder>
                  <Stack gap="xs">
                    <Group grow gap="xs">
                      <TextInput
                        required
                        label="Type"
                        placeholder="Architecte, Décorateur..."
                        {...form.getInputProps(`contacts.${index}.type`)}
                      />
                      <TextInput
                        required
                        label="Prénom"
                        {...form.getInputProps(`contacts.${index}.firstName`)}
                      />
                      <TextInput
                        required
                        label="Nom"
                        {...form.getInputProps(`contacts.${index}.lastName`)}
                      />
                      <TextInput
                        label="Email"
                        {...form.getInputProps(`contacts.${index}.email`)}
                      />
                      <TextInput
                        label="Téléphone"
                        {...form.getInputProps(`contacts.${index}.phone`)}
                      />
                      <Button
                        variant="subtle"
                        onClick={() =>
                          setExpandedContacts(prev => ({
                            ...prev,
                            [index]: !prev[index],
                          }))
                        }
                        rightSection={
                          <IconChevronDown
                            size={16}
                            style={{
                              transform: expandedContacts[index]
                                ? 'rotate(180deg)'
                                : 'rotate(0deg)',
                              transition: 'transform 200ms ease',
                            }}
                          />
                        }
                      >
                        {expandedContacts[index] ? 'Réduire' : 'Ouvrir'}
                      </Button>
                    </Group>
                    <Collapse in={expandedContacts[index]}>
                      <Stack mt="md" gap="xs">
                        <Text>Adresse</Text>
                        <Group grow>
                          <TextInput
                            label="Nom/Raison sociale"
                            {...form.getInputProps(
                              `contacts.${index}.address.address1`,
                            )}
                          />
                          <TextInput
                            label="Numéro et nom de rue"
                            {...form.getInputProps(
                              `contacts.${index}.address.address2`,
                            )}
                          />
                          <Group grow>
                            <TextInput
                              label="Code postal"
                              {...form.getInputProps(
                                `contacts.${index}.address.postalCode`,
                              )}
                            />
                            <TextInput
                              label="Ville"
                              {...form.getInputProps(
                                `contacts.${index}.address.city`,
                              )}
                            />
                          </Group>
                        </Group>
                      </Stack>
                      <Stack mt="md" gap="xs">
                        <Text>Notifications</Text>
                        <Group>
                          <Checkbox
                            label="Devis"
                            {...form.getInputProps(
                              `contacts.${index}.quoteNotificationEnabled`,
                              {
                                type: 'checkbox',
                              },
                            )}
                          />
                          <Checkbox
                            label="Plan technique"
                            {...form.getInputProps(
                              `contacts.${index}.technicalPlanNotificationEnabled`,
                              {
                                type: 'checkbox',
                              },
                            )}
                          />
                          <Checkbox
                            label="Compte-rendu de chantier"
                            {...form.getInputProps(
                              `contacts.${index}.constructionSiteReviewNotificationEnabled`,
                              {
                                type: 'checkbox',
                              },
                            )}
                          />
                          <Checkbox
                            label="PV de chantier"
                            {...form.getInputProps(
                              `contacts.${index}.constructionSiteReportNotificationEnabled`,
                              {
                                type: 'checkbox',
                              },
                            )}
                          />
                        </Group>
                      </Stack>
                      <Button
                        mt="md"
                        variant="subtle"
                        leftSection={<IconTrash size={16} />}
                        onClick={() => {
                          const contacts = form.values.contacts || [];
                          form.setFieldValue(
                            'contacts',
                            contacts.filter((_, i) => i !== index),
                          );
                        }}
                      >
                        Supprimer
                      </Button>
                    </Collapse>
                  </Stack>
                </Card>
              ))}
            </Stack>

            <Divider my="md" />

            {/* Addresses Section with Tabs */}
            <Stack>
              <Title order={4}>Addresses</Title>
              <Tabs defaultValue="homeAddress">
                <Tabs.List>
                  <Tabs.Tab value="homeAddress">Domicile</Tabs.Tab>
                  <Tabs.Tab value="deliveryAddress">Livraison</Tabs.Tab>
                  <Tabs.Tab value="installationAddress">Installation</Tabs.Tab>
                  <Tabs.Tab value="billingAddress">Facturation</Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="homeAddress" pt="xs">
                  <AddressFields prefix="homeAddress" form={form} />
                </Tabs.Panel>

                <Tabs.Panel value="deliveryAddress" pt="xs">
                  <Group my="md">
                    <Checkbox
                      label="Spécifier une adresse de livraison"
                      checked={form.values.isDeliveryAddressOverride}
                      {...form.getInputProps('isDeliveryAddressOverride')}
                    />
                  </Group>
                  <AddressFields
                    prefix="deliveryAddress"
                    defaultAddress={computedAddresses.deliveryAddress}
                    form={form}
                    disabled={!form.values.isDeliveryAddressOverride}
                  />
                </Tabs.Panel>

                <Tabs.Panel value="installationAddress" pt="xs">
                  <Group my="md">
                    <Checkbox
                      label="Spécifier une adresse d'installation"
                      checked={form.values.isInstallationAddressOverride}
                      {...form.getInputProps('isInstallationAddressOverride')}
                    />
                  </Group>
                  <AddressFields
                    prefix="installationAddress"
                    defaultAddress={computedAddresses.installationAddress}
                    form={form}
                    disabled={!form.values.isInstallationAddressOverride}
                  />
                </Tabs.Panel>

                <Tabs.Panel value="billingAddress" pt="xs">
                  <Group my="md">
                    <Checkbox
                      label="Spécifier une adresse de facturation"
                      checked={form.values.isBillingAddressOverride}
                      {...form.getInputProps('isBillingAddressOverride')}
                    />
                  </Group>
                  <AddressFields
                    prefix="billingAddress"
                    defaultAddress={computedAddresses.billingAddress}
                    form={form}
                    disabled={!form.values.isBillingAddressOverride}
                  />
                </Tabs.Panel>
              </Tabs>
            </Stack>

            <Divider my="md" />
          </>
        )}
        {(initialValues || clientType) && (
          <Button
            type="submit"
            loading={isLoading}
            mt="md"
            leftSection={<IconDeviceFloppy size={16} />}
          >
            Enregistrer
          </Button>
        )}
      </Stack>
    </form>
  );
}
